// Central icon registry. The whole app renders icons through <Icon name=… />,
// backed by Font Awesome (free solid + a few regular outlines). Keeping the
// glyph→icon mapping in one place means a single import for callers and a single
// source of truth if we ever re-skin the icon set.
//
// Usage:
//   <Icon name="bell" />
//   <Icon name="favorite" className="text-amber-500" />
//   <Icon name="sync" spin />
//
// Icons inherit the surrounding font-size/color (they render at 1em currentColor),
// so size them with text-* / color utilities just like the emoji they replaced.

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFile, faFileLines, faFolder, faFolderOpen, faFolderTree, faBook, faBookOpen, faLayerGroup,
  faBell, faWandMagicSparkles, faCircleHalfStroke, faSun, faPlus, faMagnifyingGlass, faDownload,
  faComment, faComments, faBullseye, faArrowsRotate, faArrowRotateLeft, faFlag,
  faCalendarDay, faNoteSticky, faPaperclip, faCircleCheck, faCircleXmark, faCheck,
  faEnvelope, faEye, faEyeSlash, faClock, faClockRotateLeft, faTriangleExclamation, faBan, faCircle,
  faRocket, faRobot, faBullhorn, faLink, faBoxArchive, faTrashCan, faGear, faTrafficLight,
  faTableColumns, faTableCells, faImage, faMoneyBillWave, faCodeBranch, faTicket,
  faMountainSun, faPersonRunning, faSquareCheck, faQuoteLeft, faLock, faUsers, faGlobe,
  faHourglassHalf, faSpinner, faUserCheck, faStar, faDiamond, faList, faChartColumn,
  faLightbulb, faMinus, faTable, faInbox, faCircleInfo, faBuilding, faHandshake, faUser,
  faClone, faGripVertical, faBug, faHeadset, faPuzzlePiece, faCrown, faTurnDown,
  faChevronRight, faChevronDown, faChevronUp, faChevronLeft,
  faCaretRight, faCaretDown, faCaretUp,
  faArrowRight, faArrowLeft, faArrowUp, faArrowDown, faUpRightFromSquare, faRightLeft,
  faArrowUpFromBracket, faXmark, faEllipsis, faPen, faCheckDouble, faReply, faFaceSmile,
  faExpand, faCompress,
} from '@fortawesome/free-solid-svg-icons';
import {
  faStar as faStarOutline, faCircle as faCircleOutline, faSquare as faSquareOutline,
} from '@fortawesome/free-regular-svg-icons';

/** Semantic name → Font Awesome icon. Add new names here, never inline elsewhere. */
export const ICONS = {
  // --- Docs: spaces / folders / documents / pages ---
  docs: faBook,
  'docs-open': faBookOpen,
  space: faLayerGroup,
  folder: faFolder,
  'folder-open': faFolderOpen,
  'folder-tree': faFolderTree,
  document: faFileLines,
  page: faFile,
  subpage: faFileLines,

  // --- Docs sidebar views ---
  'all-docs': faFolderTree,
  'assigned-to-me': faUserCheck,
  'review-queue': faMagnifyingGlass,
  recent: faClockRotateLeft,
  favorite: faStar,
  'favorite-outline': faStarOutline,

  // --- Visibility ---
  private: faLock,
  team: faUsers,
  company: faGlobe,

  // --- App shell / global ---
  bell: faBell,
  ai: faWandMagicSparkles,
  theme: faCircleHalfStroke,
  'morning-brief': faSun,
  create: faPlus,
  search: faMagnifyingGlass,
  settings: faGear,
  'more': faEllipsis,
  expand: faExpand,
  compress: faCompress,

  // --- Tickets / work items ---
  ticket: faTicket,
  // Work-item types (match ticket TYPE_ICON in lib/format.ts)
  support: faHeadset,
  bug: faBug,
  feature: faPuzzlePiece,
  epic: faCrown,
  subtask: faTurnDown,
  sprint: faPersonRunning,
  release: faRocket,
  'release-peak': faMountainSun,
  board: faTableColumns,
  grid: faTableCells,
  list: faList,
  table: faTable,
  callout: faLightbulb,
  divider: faMinus,
  branch: faCodeBranch,
  money: faMoneyBillWave,
  watch: faEye,
  reveal: faEye,
  hide: faEyeSlash,
  chart: faChartColumn,

  // --- Notifications / activity ---
  comment: faComment,
  comments: faComments,
  reply: faReply,
  'add-reaction': faFaceSmile,
  assigned: faBullseye,
  sync: faArrowsRotate,
  priority: faFlag,
  flag: faFlag,
  due: faCalendarDay,
  calendar: faCalendarDay,
  note: faNoteSticky,
  attach: faPaperclip,
  reopen: faArrowRotateLeft,
  closed: faCircleCheck,
  'customer-reply': faEnvelope,
  message: faEnvelope,
  'sla-warning': faClock,
  alert: faTriangleExclamation,
  warning: faTriangleExclamation,
  blocked: faBan,
  'build-passed': faCircleCheck,
  'build-failed': faCircleXmark,
  deploy: faRocket,
  agent: faRobot,
  robot: faRobot,
  gate: faTrafficLight,
  announce: faBullhorn,
  archive: faBoxArchive,

  // --- Status / state ---
  check: faCheck,
  'check-all': faCheckDouble,
  done: faCircleCheck,
  'circle-check': faCircleCheck,
  'circle-x': faCircleXmark,
  pending: faHourglassHalf,
  clock: faClock,
  spinner: faSpinner,
  'dot-filled': faCircle,
  'dot-empty': faCircleOutline,
  tasks: faSquareCheck,
  'checkbox-empty': faSquareOutline,
  quote: faQuoteLeft,
  diamond: faDiamond,
  empty: faInbox,
  info: faCircleInfo,
  user: faUser,
  customer: faBuilding,
  promise: faHandshake,

  // --- Common actions ---
  link: faLink,
  copy: faClone,
  edit: faPen,
  duplicate: faClone,
  grip: faGripVertical,
  drag: faGripVertical,
  trash: faTrashCan,
  image: faImage,
  close: faXmark,
  upload: faArrowUpFromBracket,
  download: faDownload,
  refresh: faArrowsRotate,
  external: faUpRightFromSquare,

  // --- Carets (expand / collapse / sort) ---
  'caret-right': faCaretRight,
  'caret-down': faCaretDown,
  'caret-up': faCaretUp,
  'chevron-right': faChevronRight,
  'chevron-down': faChevronDown,
  'chevron-up': faChevronUp,
  'chevron-left': faChevronLeft,

  // --- Arrows ---
  'arrow-right': faArrowRight,
  'arrow-left': faArrowLeft,
  'arrow-up': faArrowUp,
  'arrow-down': faArrowDown,
  'arrow-up-right': faUpRightFromSquare,
  swap: faRightLeft,
} satisfies Record<string, IconDefinition>;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  className?: string;
  /** Reserve a uniform 1.25em box so icons in a list align (like FA fixed-width). */
  fixedWidth?: boolean;
  spin?: boolean;
  title?: string;
  /** Decorative by default; pass a label to expose it to assistive tech. */
  label?: string;
}

export function Icon({ name, className, fixedWidth, spin, title, label }: IconProps) {
  return (
    <FontAwesomeIcon
      icon={ICONS[name]}
      className={className}
      fixedWidth={fixedWidth}
      spin={spin}
      title={title}
      aria-hidden={label ? undefined : true}
      aria-label={label}
    />
  );
}
